import { Send, CheckCircle, Clock, AlertTriangle, XCircle, Trash2, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatLongDate as formatDate, formatCurrency } from '@/lib/format';

const statusConfig: Record<string, { label: string; icon: typeof CheckCircle; color: string; bgColor: string; iconColor: string }> = {
  draft: { label: 'Draft', icon: Clock, color: 'text-foreground', bgColor: 'bg-muted', iconColor: 'text-muted-foreground' },
  sent: { label: 'Sent', icon: Send, color: 'text-foreground', bgColor: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600' },
  paid: { label: 'Paid', icon: CheckCircle, color: 'text-foreground', bgColor: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600' },
  overdue: { label: 'Overdue', icon: AlertTriangle, color: 'text-foreground', bgColor: 'bg-red-100 dark:bg-red-900/30', iconColor: 'text-red-600' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-foreground', bgColor: 'bg-muted', iconColor: 'text-muted-foreground' },
};

interface Invoice {
  id: string;
  status: string;
  parentEmail: string | null;
  description: string | null;
  dueDate: string;
  amountCents: number;
  currency: string;
}

interface InvoiceTableProps {
  invoices: Invoice[];
  onSend: (id: string) => void;
  onMarkPaid: (id: string) => void;
  onDelete: (id: string) => void;
  isSending: boolean;
  isDeleting: boolean;
}

export function InvoiceTable({
  invoices,
  onSend,
  onMarkPaid,
  onDelete,
  isSending,
  isDeleting,
}: InvoiceTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice List</CardTitle>
        <CardDescription>Manage billing for parents</CardDescription>
      </CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <div className="text-center py-8">
            <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No invoices found</p>
            <p className="text-sm text-gray-400">Create your first invoice to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => {
              const config = statusConfig[invoice.status] || statusConfig.draft;
              const StatusIcon = config.icon;
              const isOverdue = invoice.status === 'sent' && new Date(invoice.dueDate) < new Date();

              return (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 bg-muted/40 rounded-lg hover:bg-muted/70 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn('p-2 rounded-full flex-shrink-0', config.bgColor)}>
                      <StatusIcon className={cn('w-5 h-5', config.iconColor)} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">
                        {invoice.parentEmail || 'Unknown Parent'}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">
                        {invoice.description || 'No description'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Due: {formatDate(invoice.dueDate)}
                        {isOverdue && <span className="text-red-500 ml-2">(Overdue)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="font-bold text-foreground">
                        {formatCurrency(invoice.amountCents, invoice.currency)}
                      </div>
                      <div className={cn('text-xs font-medium', config.iconColor)}>
                        {config.label}
                      </div>
                    </div>
                    <div className="flex gap-1 sm:gap-2">
                      {invoice.status === 'draft' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onSend(invoice.id)}
                            disabled={isSending}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDelete(invoice.id)}
                            disabled={isDeleting}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onMarkPaid(invoice.id)}
                          disabled={isSending}
                          className="text-green-600"
                        >
                          <CheckCircle className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Mark Paid</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { statusConfig, formatCurrency, formatDate };
